import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as WordIcon,
  TableChart as ExcelIcon,
  Email as MsgIcon,
  Visibility as PreviewIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius } from '../../../constants';
import {
  formatFileSize,
  getFileName,
  getFileUrl,
  isImageFile,
  isPdfFile,
  isWordFile,
  isExcelFile,
  isMsgFile,
} from '../../../utils/fileUtils';
import { getTextareaStyle, getHelperTextStyles } from '../../../utils/formUtils';
import SectionCard from '../raiseQueryForm/SectionCard';
import FormFieldLabel from '../raiseQueryForm/FormFieldLabel';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import {
  ACCEPTED_FILE_INPUT_TYPES,
  ALLOWED_FILE_TYPES_LABEL,
  MAX_TOTAL_SIZE,
} from '../../../constants/raiseQueryForm';

const getFileIcon = (file) => {
  if (isImageFile(file)) return <ImageIcon sx={{ fontSize: '18px', color: '#10B981' }} />;
  if (isPdfFile(file)) return <PdfIcon sx={{ fontSize: '18px', color: '#EF4444' }} />;
  if (isWordFile(file)) return <WordIcon sx={{ fontSize: '18px', color: '#3B82F6' }} />;
  if (isExcelFile(file)) return <ExcelIcon sx={{ fontSize: '18px', color: '#10B981' }} />;
  if (isMsgFile(file)) return <MsgIcon sx={{ fontSize: '18px', color: '#F59E0B' }} />;
  return <FileIcon sx={{ fontSize: '18px', color: '#64748b' }} />;
};

const canPreview = (file) => isImageFile(file) || isPdfFile(file);

const EditQueryDetailsSection = ({
  formData,
  handleChange,
  handleFocus,
  handleBlur,
  focusedFields,
  errors,
  existingAttachments,
  newFiles,
  attachmentInputKey,
  onNewFilesChange,
  onRemoveExistingAttachment,
  onRemoveNewFile,
}) => {
  const helperTextBaseStyles = getHelperTextStyles();
  const maxFileCount = null;
  const fileInputRef = useRef(null);
  const [pendingDeleteAttachment, setPendingDeleteAttachment] = useState(null);

  const textareaStyles = getTextareaStyle(
    'queryDescription',
    !!errors.queryDescription,
    focusedFields.queryDescription
  );

  const newFilePreviews = useMemo(
    () =>
      newFiles.map((file) => {
        if (!isImageFile(file)) return '';
        return URL.createObjectURL(file);
      }),
    [newFiles]
  );

  useEffect(() => {
    return () => {
      newFilePreviews.forEach((previewUrl) => {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
      });
    };
  }, [newFilePreviews]);

  const totalNewFileSize = useMemo(
    () => newFiles.reduce((sum, file) => sum + (file.size || 0), 0),
    [newFiles]
  );

  const totalExistingFileSize = useMemo(
    () => existingAttachments.reduce((sum, file) => sum + (file.fileSize || file.size || 0), 0),
    [existingAttachments]
  );

  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleConfirmRemoveExisting = () => {
    if (pendingDeleteAttachment) {
      onRemoveExistingAttachment(pendingDeleteAttachment);
    }
    setPendingDeleteAttachment(null);
  };

  const renderAttachmentCard = ({
    key,
    file,
    fileName,
    fileSize,
    previewUrl,
    onPreview,
    onRemove,
    removeIcon,
  }) => (
    <Box
      key={key}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.xs,
        padding: `${spacing.xs} ${spacing.xs}`,
        backgroundColor: '#f8f9fb',
        border: '1px solid #e5e7eb',
        borderRadius: borderRadius.sm,
        transition: 'all 0.15s ease-in-out',
        minWidth: 0,
        overflow: 'hidden',
        '&:hover': {
          borderColor: '#cbd5e1',
          backgroundColor: '#f1f3f5',
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: '32px',
          height: '32px',
          borderRadius: borderRadius.sm,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.backgroundWhite,
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}
      >
        {isImageFile(file) && previewUrl ? (
          <Box
            component="img"
            src={previewUrl}
            alt={fileName}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: borderRadius.sm,
            }}
          />
        ) : (
          getFileIcon(file)
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Tooltip title={fileName} placement="top" arrow enterDelay={400}>
          <Typography
            sx={{
              fontSize: fonts.fontSizeSm,
              fontWeight: fonts.weightMedium,
              color: '#1e293b',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.2,
            }}
          >
            {fileName}
          </Typography>
        </Tooltip>
        <Typography
          sx={{
            fontSize: fonts.fontSizeXs,
            color: '#94a3b8',
            lineHeight: 1.2,
          }}
        >
          {formatFileSize(fileSize)}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {canPreview(file) && (
          <Tooltip title="Preview" placement="top" arrow>
            <IconButton
              size="small"
              onClick={onPreview}
              sx={{
                padding: spacing.xs,
                color: '#94a3b8',
                '&:hover': {
                  color: colors.primaryDark,
                  backgroundColor: 'rgba(35, 52, 145, 0.08)',
                },
              }}
            >
              <PreviewIcon sx={{ fontSize: '15px' }} />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Remove" placement="top" arrow>
          <IconButton
            size="small"
            onClick={onRemove}
            sx={{
              padding: spacing.xs,
              color: '#94a3b8',
              '&:hover': {
                color: colors.error,
                backgroundColor: 'rgba(239, 68, 68, 0.08)',
              },
            }}
          >
            {removeIcon}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <SectionCard title="Query Details">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, width: '100%', boxSizing: 'border-box' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
          <FormFieldLabel label="Query Description" required={true} />
          <textarea
            placeholder="Provide a detailed description"
            value={formData.queryDescription}
            onChange={handleChange('queryDescription')}
            onFocus={handleFocus('queryDescription')}
            onBlur={handleBlur('queryDescription')}
            rows={3}
            style={{
              ...textareaStyles,
              width: '100%',
              boxSizing: 'border-box',
              maxWidth: '100%',
            }}
          />
          <Typography
            variant="caption"
            sx={{
              ...helperTextBaseStyles,
              color: errors.queryDescription ? colors.error : colors.textPrimaryDark,
            }}
          >
            {errors.queryDescription || 'Provide a detailed description of the query'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
          <FormFieldLabel label="Add New Attachments" />
          <Box
            onClick={handleDropZoneClick}
            sx={{
              width: '100%',
              minHeight: '80px',
              border: `1.5px dashed ${colors.borderDefault}`,
              borderRadius: borderRadius.md,
              backgroundColor: colors.backgroundWhite,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.xs,
              paddingBottom: spacing.xs,
              boxSizing: 'border-box',
              '&:hover': {
                borderColor: colors.primaryDark,
                backgroundColor: colors.primarySoft,
              },
            }}
          >
            <input
              ref={fileInputRef}
              key={attachmentInputKey}
              type="file"
              multiple
              accept={ACCEPTED_FILE_INPUT_TYPES}
              onChange={(event) => onNewFilesChange(event.target.files)}
              style={{ display: 'none' }}
            />
            <CloudUploadIcon
              sx={{
                fontSize: fonts.fontSize3xl,
                color: '#64748b',
              }}
            />
            <Typography
              sx={{
                fontSize: fonts.fontSizeXs,
                color: '#94a3b8',
                textAlign: 'center',
                lineHeight: 1.2,
              }}
            >
              Click to upload
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  ...helperTextBaseStyles,
                  color: colors.textSecondary,
                  textAlign: 'center',
                  fontSize: fonts.fontSizeXs,
                  lineHeight: fonts.lineHeightTight,
                }}
              >
                Allowed file types: {ALLOWED_FILE_TYPES_LABEL}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  ...helperTextBaseStyles,
                  color: colors.textSecondary,
                  textAlign: 'center',
                  fontSize: fonts.fontSizeXs,
                  lineHeight: fonts.lineHeightTight,
                }}
              >
                Max total upload size: {MAX_TOTAL_SIZE / (1024 * 1024)}MB (combined)
                {maxFileCount ? ` • Max files: ${maxFileCount}` : ''}
              </Typography>
            </Box>
          </Box>
        </Box>

        {existingAttachments.length > 0 && (
          <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
            <FormFieldLabel label="Existing Attachments" />
            <Typography
              variant="caption"
              sx={{
                fontSize: fonts.fontSizeSm,
                color: colors.textSecondary,
                fontWeight: fonts.weightMedium,
                mb: spacing.xs,
                display: 'block',
              }}
            >
              {existingAttachments.length} file{existingAttachments.length !== 1 ? 's' : ''} attached
              {totalExistingFileSize > 0 ? ` • ${formatFileSize(totalExistingFileSize)}` : ''}
            </Typography>

            <Box
              sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                gap: spacing.xs,
                boxSizing: 'border-box',
              }}
            >
              {existingAttachments.map((file, index) => {
                const name = file.fileName || getFileName(file);
                const previewUrl = isImageFile(file) ? getFileUrl(file) : '';
                const fileUrl = getFileUrl(file);

                return renderAttachmentCard({
                  key: file._id || `${file.filePath || file.fileUrl || name}-${index}`,
                  file,
                  fileName: name,
                  fileSize: file.fileSize || file.size || 0,
                  previewUrl,
                  onPreview: (event) => {
                    event.stopPropagation();
                    if (fileUrl) {
                      window.open(fileUrl, '_blank', 'noopener,noreferrer');
                    }
                  },
                  onRemove: (event) => {
                    event.stopPropagation();
                    setPendingDeleteAttachment(file);
                  },
                  removeIcon: <DeleteIcon sx={{ fontSize: '15px' }} />,
                });
              })}
            </Box>
          </Box>
        )}

        {newFiles.length > 0 && (
          <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
            <FormFieldLabel label="New Attachments" />
            <Typography
              variant="caption"
              sx={{
                fontSize: fonts.fontSizeSm,
                color: colors.textSecondary,
                fontWeight: fonts.weightMedium,
                mb: spacing.xs,
                display: 'block',
              }}
            >
              {newFiles.length} file{newFiles.length !== 1 ? 's' : ''} selected • {formatFileSize(totalNewFileSize)}
            </Typography>

            <Box
              sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                gap: spacing.xs,
                boxSizing: 'border-box',
              }}
            >
              {newFiles.map((file, index) => {
                const previewUrl = newFilePreviews[index] || '';
                return renderAttachmentCard({
                  key: `${file.name}-${index}`,
                  file,
                  fileName: file.name,
                  fileSize: file.size || 0,
                  previewUrl,
                  onPreview: (event) => {
                    event.stopPropagation();
                    const objectUrl = URL.createObjectURL(file);
                    window.open(objectUrl, '_blank');
                  },
                  onRemove: (event) => {
                    event.stopPropagation();
                    onRemoveNewFile(index);
                  },
                  removeIcon: <CloseIcon sx={{ fontSize: '15px' }} />,
                });
              })}
            </Box>
          </Box>
        )}
      </Box>

      <ConfirmationDialog
        open={Boolean(pendingDeleteAttachment)}
        onClose={() => setPendingDeleteAttachment(null)}
        onConfirm={handleConfirmRemoveExisting}
        title="Remove Attachment"
        message="Are you sure you want to remove this attachment from the query?"
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
      />
    </SectionCard>
  );
};

export default EditQueryDetailsSection;
